/**
 * attestationService — KTRUST-L1.
 *
 * Client-side wrapper for the attestationSigner Cloud Function.
 * Signing occurs exclusively in Cloud Functions (server-side only).
 * This service layer provides the attest() call that all substrates use.
 *
 * substrate_origin enum: aegis | kaudit | argus | kcc | kcrit | doc1 | manual
 */

const CF_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_KTRUST_SIGNER_URL) || null;

export const SUBSTRATE_ORIGIN = Object.freeze({
  AEGIS:   'aegis',
  KAUDIT:  'kaudit',
  ARGUS:   'argus',
  KCC:     'kcc',
  KCRIT:   'kcrit',
  DOC1:    'doc1',
  MANUAL:  'manual',
});

const REQUIRED_FIELDS = ['tenantId', 'claim', 'actorId', 'actorRole', 'substrateOrigin'];

function validateAttestParams(params) {
  const missing = REQUIRED_FIELDS.filter(f => !params?.[f]);
  if (missing.length > 0) {
    throw new Error(`attestationService.attest: missing required fields: ${missing.join(', ')}`);
  }
  if (!Object.values(SUBSTRATE_ORIGIN).includes(params.substrateOrigin)) {
    throw new Error(`attestationService.attest: invalid substrateOrigin "${params.substrateOrigin}". Must be one of: ${Object.values(SUBSTRATE_ORIGIN).join(', ')}`);
  }
}

const attestationService = {
  /**
   * Create a signed attestation via Cloud Function.
   *
   * @param {object} params
   * @param {string} params.tenantId
   * @param {string} params.claim           - human-readable claim text
   * @param {string[]} [params.evidenceRefs] - links to DOC-1 provenance entries, source events, etc.
   * @param {string} params.actorId
   * @param {string} params.actorRole
   * @param {string} [params.parentAttestationId] - for chained attestations
   * @param {string} params.substrateOrigin  - SUBSTRATE_ORIGIN enum value
   * @returns {Promise<{ attestationId: string, mocked?: boolean }>}
   */
  async attest(params) {
    validateAttestParams(params);

    if (!CF_URL) {
      return {
        attestationId: `attest_mock_${Date.now().toString(36)}`,
        mocked: true,
        substrateOrigin: params.substrateOrigin,
        tenantId: params.tenantId,
      };
    }

    try {
      const res = await fetch(CF_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId:             params.tenantId,
          claim:                params.claim,
          evidenceRefs:         params.evidenceRefs || [],
          actorId:              params.actorId,
          actorRole:            params.actorRole,
          parentAttestationId:  params.parentAttestationId || null,
          substrateOrigin:      params.substrateOrigin,
        }),
      });
      if (!res.ok) {
        throw new Error(`attestationService.attest: HTTP ${res.status}`);
      }
      return await res.json();
    } catch (err) {
      return {
        attestationId: null,
        mocked: false,
        failed: true,
        error: err.message,
        substrateOrigin: params.substrateOrigin,
        tenantId: params.tenantId,
      };
    }
  },
};

export default attestationService;
